import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, downloadFile, getToken, type Participant } from "../lib/api";
import { useFetch } from "../lib/useFetch";
import VersionedFormSection from "../components/VersionedFormSection";

/** Only the header summary reads plans here; the section itself owns the rest. */
interface CarePlan { id: number; version: number; status: string }
interface CaseNote {
  id: number; body: string; isUrgent: boolean; aiAssisted: boolean; authorName: string; createdAt: string;
}
interface Goal {
  id: number; title: string; description: string | null; targetMethod: string | null;
  reviewDate: string | null; status: string;
  progress: { id: number; note: string; recordedByName: string; createdAt: string }[];
}
interface Doc {
  id: number; title: string; category: string; fileName: string; sizeBytes: number;
  expiryDate: string | null; showToWorker: boolean; expired: boolean; expiringSoon: boolean;
}
interface Fund {
  id: number; managedBy: string; totalCents: number; spentCents: number;
  utilisation: number; warnings: string[]; startDate: string; endDate: string;
  progressDate: string; reviewDate: string; status: string;
}

const fmt = (iso: string) => new Date(iso).toLocaleString("en-AU", { dateStyle: "medium", timeStyle: "short" });

export default function ParticipantDetail() {
  const { id } = useParams();
  const participant = useFetch<Participant>(`/v1/participants/${id}`);
  const plans = useFetch<CarePlan[]>(`/v1/participants/${id}/care-plans`);
  const notes = useFetch<CaseNote[]>(`/v1/participants/${id}/case-notes`);
  const fundsData = useFetch<Fund[]>(`/v1/participants/${id}/funds`);
  const goals = useFetch<Goal[]>(`/v1/participants/${id}/goals`);
  const docs = useFetch<Doc[]>(`/v1/participants/${id}/documents`);

  const [goalTitle, setGoalTitle] = useState("");
  const [progressText, setProgressText] = useState<Record<number, string>>({});
  const [docForm, setDocForm] = useState({ title: "", category: "general", expiryDate: "", showToWorker: false });
  const [docFile, setDocFile] = useState<File | null>(null);
  const [docError, setDocError] = useState<string | null>(null);

  async function addGoal(e: React.FormEvent) {
    e.preventDefault();
    if (goalTitle.trim().length < 3) return;
    await api(`/v1/participants/${id}/goals`, { method: "POST", body: JSON.stringify({ title: goalTitle }) });
    setGoalTitle(""); goals.reload();
  }
  async function addProgress(goalId: number) {
    const note = progressText[goalId]?.trim();
    if (!note || note.length < 3) return;
    await api(`/v1/goals/${goalId}/progress`, { method: "POST", body: JSON.stringify({ note }) });
    setProgressText((t) => ({ ...t, [goalId]: "" })); goals.reload();
  }
  async function setGoalStatus(goalId: number, status: string) {
    await api(`/v1/goals/${goalId}/status`, { method: "POST", body: JSON.stringify({ status }) });
    goals.reload();
  }
  async function uploadDoc(e: React.FormEvent) {
    e.preventDefault();
    if (!docFile) { setDocError("Choose a file"); return; }
    const form = new FormData();
    form.append("file", docFile);
    if (docForm.title) form.append("title", docForm.title);
    form.append("category", docForm.category);
    if (docForm.expiryDate) form.append("expiryDate", docForm.expiryDate);
    form.append("showToWorker", String(docForm.showToWorker));
    const res = await fetch(`/v1/participants/${id}/documents`, {
      method: "POST",
      headers: { authorization: `Bearer ${getToken()}` },
      body: form,
    });
    if (!res.ok) { setDocError("Upload failed"); return; }
    setDocForm({ title: "", category: "general", expiryDate: "", showToWorker: false });
    setDocFile(null); setDocError(null); docs.reload();
  }
  const downloadDoc = (docId: number, fileName: string) =>
    downloadFile(`/v1/documents/${docId}/download`, fileName);

  const [showFundForm, setShowFundForm] = useState(false);
  const [fundForm, setFundForm] = useState({ managedBy: "ndia", total: "", startDate: "", endDate: "" });
  const [fundError, setFundError] = useState<string | null>(null);

  async function addFund(e: React.FormEvent) {
    e.preventDefault();
    const totalCents = Math.round(Number(fundForm.total) * 100);
    if (!totalCents || totalCents <= 0) { setFundError("Enter a fund amount"); return; }
    try {
      await api(`/v1/participants/${id}/funds`, {
        method: "POST",
        body: JSON.stringify({ managedBy: fundForm.managedBy, totalCents, startDate: fundForm.startDate, endDate: fundForm.endDate }),
      });
      setShowFundForm(false); setFundError(null);
      setFundForm({ managedBy: "ndia", total: "", startDate: "", endDate: "" });
      fundsData.reload();
    } catch (err) { setFundError((err as Error).message); }
  }

  const [noteText, setNoteText] = useState("");
  const [noteUrgent, setNoteUrgent] = useState(false);
  const [noteError, setNoteError] = useState<string | null>(null);
  const [noteAiAssisted, setNoteAiAssisted] = useState(false);
  const [drafting, setDrafting] = useState(false);

  async function draftWithAi() {
    if (noteText.trim().length < 5) { setNoteError("Type rough notes first, then let AI structure them"); return; }
    setDrafting(true); setNoteError(null);
    try {
      const { draft } = await api<{ draft: string }>(`/v1/participants/${id}/case-notes/draft`, {
        method: "POST", body: JSON.stringify({ roughNotes: noteText }),
      });
      setNoteText(draft);
      setNoteAiAssisted(true);
    } catch (err) { setNoteError((err as Error).message); }
    finally { setDrafting(false); }
  }

  async function onboard() {
    await api(`/v1/participants/${id}/onboard`, { method: "POST", body: "{}" });
    participant.reload();
  }
  async function addNote(e: React.FormEvent) {
    e.preventDefault();
    if (noteText.trim().length < 3) { setNoteError("Write the note first"); return; }
    try {
      await api(`/v1/participants/${id}/case-notes`, {
        method: "POST", body: JSON.stringify({ body: noteText, isUrgent: noteUrgent, aiAssisted: noteAiAssisted }),
      });
      setNoteText(""); setNoteUrgent(false); setNoteAiAssisted(false); setNoteError(null); notes.reload();
    } catch (err) { setNoteError((err as Error).message); }
  }

  const p = participant.data;
  if (participant.loading) return <div className="space-y-3"><div className="skeleton h-8 w-48" /><div className="skeleton h-24" /><div className="skeleton h-24" /></div>;
  if (participant.error || !p) return <p className="text-sm text-red-600">{participant.error ?? "Not found"}</p>;

  return (
    <div className="space-y-6">
      <Link to="/participants" className="text-sm text-mut hover:text-teal-700">← Participants</Link>

      <div className="card p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="h-12 w-12 rounded-full bg-teal-50 flex items-center justify-center text-teal-700 font-semibold">
              {p.firstName[0]}{p.lastName[0]}
            </div>
            <div>
              <h1 className="text-xl font-semibold text-ink">{p.firstName} {p.lastName}</h1>
              <p className="text-sm text-mut font-mono">{p.ndisNumber ?? "No NDIS number"}</p>
            </div>
          </div>
          {p.status !== "onboarded" && (
            <button onClick={onboard} className="btn-primary">Onboard</button>
          )}
        </div>
        <dl className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
          <div><dt className="text-mut">Status</dt><dd className="font-medium text-ink">{p.status}</dd></div>
          <div><dt className="text-mut">Email</dt><dd className="font-medium text-ink">{p.email ?? "—"}</dd></div>
          <div><dt className="text-mut">Phone</dt><dd className="font-medium text-ink">{p.phone ?? "—"}</dd></div>
          <div><dt className="text-mut">Active plan</dt>
            <dd className="font-medium text-ink">{plans.data?.find((x) => x.status === "active") ? `v${plans.data.find((x) => x.status === "active")!.version}` : "None"}</dd></div>
        </dl>
      </div>

      <section className="card p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-medium text-ink">Funding</h2>
          <button onClick={() => setShowFundForm((v) => !v)}
            className="btn-secondary px-3 py-1.5">
            {showFundForm ? "Cancel" : "Add fund"}
          </button>
        </div>
        {showFundForm && (
          <form onSubmit={addFund} className="grid grid-cols-2 sm:grid-cols-5 gap-3 items-end">
            <label className="block"><span className="label">Managed by</span>
              <select value={fundForm.managedBy} onChange={(e) => setFundForm({ ...fundForm, managedBy: e.target.value })}
                className="input">
                <option value="ndia">NDIA-managed</option>
                <option value="plan">Plan-managed</option>
                <option value="self">Self-managed</option>
              </select></label>
            <label className="block"><span className="label">Total (AUD)</span>
              <input type="number" min="1" step="0.01" value={fundForm.total}
                onChange={(e) => setFundForm({ ...fundForm, total: e.target.value })}
                className="input" /></label>
            <label className="block"><span className="label">Start</span>
              <input type="date" value={fundForm.startDate} onChange={(e) => setFundForm({ ...fundForm, startDate: e.target.value })}
                className="input" /></label>
            <label className="block"><span className="label">End</span>
              <input type="date" value={fundForm.endDate} onChange={(e) => setFundForm({ ...fundForm, endDate: e.target.value })}
                className="input" /></label>
            <button className="btn-dark">Save</button>
            {fundError && <p className="text-sm text-red-600 col-span-full">{fundError}</p>}
          </form>
        )}
        {fundsData.data?.length === 0 && !showFundForm && <p className="text-sm text-faint">No funding recorded.</p>}
        <div className="space-y-3">
          {fundsData.data?.map((f) => {
            const pct = Math.min(100, Math.round(f.utilisation * 100));
            const barColor = f.utilisation >= 1 ? "bg-red-500" : f.utilisation >= 0.8 ? "bg-amber-500" : "bg-teal-600";
            return (
              <div key={f.id} className="border border-edge-soft rounded-xl p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2 text-sm">
                    <span className="font-medium text-ink capitalize">{f.managedBy}-managed</span>
                    <span className="text-faint">{f.startDate} → {f.endDate}</span>
                  </div>
                  <span className="text-sm font-medium text-ink">
                    ${(f.spentCents / 100).toLocaleString("en-AU", { minimumFractionDigits: 2 })} / ${(f.totalCents / 100).toLocaleString("en-AU", { minimumFractionDigits: 2 })}
                  </span>
                </div>
                <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                  <div className={`h-full ${barColor}`} style={{ width: `${pct}%` }} />
                </div>
                <div className="mt-2 flex items-center gap-3 text-xs">
                  <span className="text-mut">{pct}% utilised</span>
                  <span className="text-faint">Review {f.reviewDate}</span>
                  {f.warnings.map((w) => (
                    <span key={w} className="chip bg-amber-50 text-amber-700">{w}</span>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <section className="card p-6 space-y-4">
        <h2 className="text-lg font-medium text-ink">Goals</h2>
        <form onSubmit={addGoal} className="flex gap-2">
          <input value={goalTitle} onChange={(e) => setGoalTitle(e.target.value)}
            placeholder="New goal, e.g. Independent grocery shopping" className="input mt-0 flex-1" />
          <button className="btn-dark shrink-0">Add goal</button>
        </form>
        {goals.data?.length === 0 && <p className="text-sm text-faint">No goals yet.</p>}
        <div className="space-y-3">
          {goals.data?.map((g) => (
            <div key={g.id} className="border border-edge-soft rounded-xl p-4 space-y-2">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-ink">{g.title}</span>
                <span className={`chip ${g.status === "achieved" ? "bg-teal-50 text-teal-800" : g.status === "paused" ? "bg-slate-100 text-body" : "bg-sky-50 text-sky-700"}`}>{g.status}</span>
                {g.reviewDate && <span className="text-xs text-faint">review {g.reviewDate}</span>}
                {g.status === "active" && (
                  <button onClick={() => setGoalStatus(g.id, "achieved")} className="ml-auto btn-secondary px-2.5 py-1 text-xs">Mark achieved</button>
                )}
              </div>
              {g.progress.map((pr) => (
                <p key={pr.id} className="text-sm text-body border-l-2 border-teal-200 pl-3">
                  {pr.note} <span className="text-xs text-faint">— {pr.recordedByName}</span>
                </p>
              ))}
              {g.status === "active" && (
                <div className="flex gap-2">
                  <input value={progressText[g.id] ?? ""} onChange={(e) => setProgressText((t) => ({ ...t, [g.id]: e.target.value }))}
                    placeholder="Record progress…" className="input mt-0 flex-1 text-xs py-1.5" />
                  <button onClick={() => addProgress(g.id)} className="btn-secondary px-3 py-1.5 text-xs shrink-0">Log</button>
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      <section className="card p-6 space-y-4">
        <h2 className="text-lg font-medium text-ink">Documents</h2>
        <form onSubmit={uploadDoc} className="grid grid-cols-2 sm:grid-cols-5 gap-3 items-end">
          <label className="block col-span-2 sm:col-span-1"><span className="label">File</span>
            <input type="file" onChange={(e) => setDocFile(e.target.files?.[0] ?? null)}
              className="mt-1 block w-full text-xs text-mut file:mr-2 file:btn-secondary file:px-3 file:py-1.5 file:text-xs file:border-0" /></label>
          <label className="block"><span className="label">Title</span>
            <input value={docForm.title} onChange={(e) => setDocForm({ ...docForm, title: e.target.value })}
              placeholder="Defaults to filename" className="input" /></label>
          <label className="block"><span className="label">Category</span>
            <select value={docForm.category} onChange={(e) => setDocForm({ ...docForm, category: e.target.value })} className="input">
              {["general", "service_agreement", "medical_consent", "intake", "reporting"].map((x) => <option key={x} value={x}>{x.replace("_", " ")}</option>)}
            </select></label>
          <label className="block"><span className="label">Expiry</span>
            <input type="date" value={docForm.expiryDate} onChange={(e) => setDocForm({ ...docForm, expiryDate: e.target.value })} className="input" /></label>
          <div className="flex items-center gap-2 pb-2">
            <label className="flex items-center gap-2 text-xs text-body">
              <input type="checkbox" checked={docForm.showToWorker} onChange={(e) => setDocForm({ ...docForm, showToWorker: e.target.checked })} />
              Workers
            </label>
            <button className="btn-dark px-3 py-1.5 text-xs">Upload</button>
          </div>
          {docError && <p className="text-sm text-red-600 col-span-full">{docError}</p>}
        </form>
        {docs.data?.length === 0 && <p className="text-sm text-faint">No documents.</p>}
        <div className="space-y-2">
          {docs.data?.map((d) => (
            <div key={d.id} className="flex items-center gap-3 border border-edge-soft rounded-xl px-4 py-2.5">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-ink truncate">{d.title}</p>
                <p className="text-xs text-faint">{d.category.replace("_", " ")} · {(d.sizeBytes / 1024).toFixed(0)} KB{d.expiryDate ? ` · expires ${d.expiryDate}` : ""}</p>
              </div>
              {d.expired && <span className="chip bg-red-100 text-red-700">expired</span>}
              {!d.expired && d.expiringSoon && <span className="chip bg-amber-50 text-amber-700">expiring soon</span>}
              {d.showToWorker && <span className="chip bg-teal-50 text-teal-800">worker-visible</span>}
              <button onClick={() => downloadDoc(d.id, d.fileName)} className="btn-secondary px-3 py-1.5 text-xs shrink-0">Download</button>
            </div>
          ))}
        </div>
      </section>

      <VersionedFormSection kind="care_plan" participantId={id!} participantName={`${p.firstName} ${p.lastName}`} />

      <VersionedFormSection kind="risk_assessment" participantId={id!} participantName={`${p.firstName} ${p.lastName}`} />

      <section className="card p-6 space-y-4">
        <h2 className="text-lg font-medium text-ink">Case notes</h2>
        <form onSubmit={addNote} className="space-y-2">
          <textarea value={noteText} onChange={(e) => setNoteText(e.target.value)} rows={3}
            placeholder="What happened during the shift? Progress, observations, follow-ups…"
            className="input mt-0" />
          {noteAiAssisted && (
            <p className="text-xs rounded-lg bg-violet-50 text-violet-700 px-3 py-2">
              AI draft — review and edit before saving. It will be recorded as AI-assisted.
            </p>
          )}
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 text-sm text-body">
              <input type="checkbox" checked={noteUrgent} onChange={(e) => setNoteUrgent(e.target.checked)} />
              Urgent — notify coordinators
            </label>
            <button type="button" onClick={draftWithAi} disabled={drafting}
              className="btn-ai">
              {drafting ? "Drafting…" : "✦ Draft with AI"}
            </button>
            <button className="btn-dark">Add note</button>
          </div>
          {noteError && <p className="text-sm text-red-600">{noteError}</p>}
        </form>
        {notes.data?.length === 0 && <p className="text-sm text-faint">No notes yet.</p>}
        <ol className="space-y-3">
          {notes.data?.map((n) => (
            <li key={n.id} className={`rounded-xl p-4 border ${n.isUrgent ? "border-red-200 bg-red-50/40" : "border-edge-soft"}`}>
              <div className="flex items-center gap-2 mb-1">
                <span className="text-sm font-medium text-ink">{n.authorName}</span>
                {n.isUrgent && <span className="chip bg-red-100 text-red-700">urgent</span>}
                {n.aiAssisted && <span className="chip bg-violet-100 text-violet-700">AI-assisted</span>}
                <span className="text-xs text-faint">{fmt(n.createdAt)}</span>
              </div>
              <p className="text-sm text-body whitespace-pre-wrap">{n.body}</p>
            </li>
          ))}
        </ol>
      </section>
    </div>
  );
}
